import React, { createContext, useContext } from 'react'
import { NarrowFulfilmentMethodInputType } from '@src/graphql-types'
import { useMarketplace } from '@src/hooks/useMarketplace'
import {
  OutletFulfilmentStateType,
  useOutletFulfilment,
} from '../outletFulfilmentAndBasketHooks/useOutletFulfilment/useOutletFulfilment'
import { useBasketTotals } from './useBasketTotals'

interface BasketTotalsContextValue {
  basketTotals: ReturnType<typeof useBasketTotals>
  minimumTotal: number | undefined
  stripeMinimumTotal: number
  disableCheckout: boolean
  checkoutRestriction: 'basketMinimum' | 'stripeMinimum' | null
}

const BasketTotalsContext = createContext<BasketTotalsContextValue | null>(null)

export const useBasketTotalsContext = (): BasketTotalsContextValue => {
  const context = useContext(BasketTotalsContext)
  if (!context) {
    throw new Error(
      'useBasketTotalsContext must be used within a BasketTotalsProvider'
    )
  }
  return context
}

// Optional version that returns null if not in provider context
export const useBasketTotalsContextOptional =
  (): BasketTotalsContextValue | null => {
    return useContext(BasketTotalsContext)
  }

interface BasketTotalsProviderProps {
  children: React.ReactNode
}

export const BasketTotalsProvider: React.FC<BasketTotalsProviderProps> = ({
  children,
}) => {
  const basketTotals = useBasketTotals()
  const marketplace = useMarketplace()
  const outletFulfilment = useOutletFulfilment({
    stateType: OutletFulfilmentStateType.GLOBAL,
  })

  const narrowType = outletFulfilment.data.currentFulfilment.narrowType
  const isDelivery = narrowType === NarrowFulfilmentMethodInputType.DELIVERY
  const isCollection = narrowType === NarrowFulfilmentMethodInputType.COLLECTION
  const stripeMinimumTotal = marketplace.stripeMinimumOrderValue

  const minimumTotal = (() => {
    if (isDelivery && !!outletFulfilment.outlet.deliveryMinimumOrderValue)
      return outletFulfilment.outlet.deliveryMinimumOrderValue
    if (isCollection && !!outletFulfilment.outlet.collectionMinimumOrderValue)
      return outletFulfilment.outlet.collectionMinimumOrderValue
  })()

  const minimumTotalReached = (() => {
    // assume true until basket totals are returned, or if theres no outlet minimum.
    if (!basketTotals.data?.checkoutBasketTotals || !minimumTotal) return true

    const orderTotal =
      basketTotals.data.checkoutBasketTotals.nonAddOnBasketItemsTotal || 0

    return orderTotal >= minimumTotal
  })()

  const stripeMinimumReached = basketTotals.data.checkoutBasketTotals
    ? basketTotals.data.checkoutBasketTotals.sumTotal >= stripeMinimumTotal
    : true

  const checkoutRestriction: 'basketMinimum' | 'stripeMinimum' | null = (() => {
    if (!minimumTotalReached) return 'basketMinimum'
    if (!stripeMinimumReached) return 'stripeMinimum'
    return null
  })()

  const disableCheckout = checkoutRestriction !== null

  return (
    <BasketTotalsContext.Provider
      value={{
        basketTotals,
        minimumTotal,
        stripeMinimumTotal,
        checkoutRestriction,
        disableCheckout,
      }}
    >
      {children}
    </BasketTotalsContext.Provider>
  )
}
